import * as THREE from 'three/webgpu';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import Locomotive from './Locomotive';
import Railcar from './Railcar';
import { RenderPipeline, WebGPURenderer } from 'three/webgpu';
import { toonOutlinePass } from 'three/tsl';
import Tracks from './Tracks';
import { IS_DEBUG_ENABLED } from './util';

export default class Interactive {
    canvas!: HTMLCanvasElement;

    scene!: THREE.Scene;
    camera!: THREE.PerspectiveCamera;
    sizes!: { width: number; height: number; pixelRatio: number };
    renderer!: WebGPURenderer;
    controls!: OrbitControls;
    postProcessing!: RenderPipeline;

    locomotive!: Locomotive;
    railcars: Railcar[] = [];
    tracks!: Tracks;
    ground!: THREE.Mesh;

    constructor() {
        this.initDom();
        this.initThree();
        this.initEvents();

        this.initLights();
        this.setupAsync();

        if (IS_DEBUG_ENABLED) {
            this.scene.add(new THREE.AxesHelper(2));
        }

        this.controls = new OrbitControls(this.camera, this.canvas);
        this.controls.enableDamping = true;
        if (!IS_DEBUG_ENABLED) {
            this.controls.enablePan = false;
            this.controls.minDistance = 5;
            this.controls.maxDistance = 20;
            this.controls.maxPolarAngle = Math.PI / 2.1;
        }
    }

    private async setupAsync(): Promise<void> {
        await this.renderer.init();

        this.tracks = new Tracks(this.scene);
        await this.tracks.create();

        this.locomotive = new Locomotive(this.scene);
        await this.locomotive.create();

        if (!IS_DEBUG_ENABLED) {
            this.initGround();
        }

        let railcarCount = 3;
        if (IS_DEBUG_ENABLED) {
            railcarCount = 0;
        }

        let currentTractor: Locomotive | Railcar = this.locomotive;
        for (let index = 0; index < railcarCount; index += 1) {
            const railcar: Railcar = new Railcar(
                this.scene,
                currentTractor,
                this.locomotive
            );
            await railcar.create();
            this.railcars.push(railcar);
            currentTractor = railcar;
        }

        this.renderer.setAnimationLoop(this.animate.bind(this));
    }

    private initGround() {
        this.ground = new THREE.Mesh(
            new THREE.PlaneGeometry(150, 150),
            new THREE.MeshStandardMaterial({ color: 0xaaaaaa })
        );
        this.ground.rotation.x = -Math.PI / 2;
        this.ground.position.y = -0.2;
        this.scene.add(this.ground);
    }

    private initDom() {
        this.canvas = document.getElementById('threejs') as HTMLCanvasElement;

        if (!this.canvas) {
            throw new Error('Canvas element with id "threejs" not found.');
        }
    }

    private initThree() {
        this.scene = new THREE.Scene();
        this.sizes = {
            width: window.innerWidth,
            height: window.innerHeight,
            pixelRatio: Math.min(window.devicePixelRatio, 2),
        };
        this.camera = new THREE.PerspectiveCamera(
            75,
            this.sizes.width / this.sizes.height,
            0.1,
            100
        );
        this.camera.position.set(0, 0, 3);
        this.scene.add(this.camera);

        if (!IS_DEBUG_ENABLED) {
            const fog = new THREE.Fog(0x87ceeb, 10, 40);
            this.scene.fog = fog;
        }

        this.renderer = new WebGPURenderer({
            canvas: this.canvas,
            antialias: true,
        });
        this.renderer.setSize(this.sizes.width, this.sizes.height);
        this.renderer.setPixelRatio(this.sizes.pixelRatio);

        this.postProcessing = new RenderPipeline(this.renderer);
        const scenePass = toonOutlinePass(
            this.scene,
            this.camera,
            new THREE.Color(0x000000),
            0.003,
            1.0
        );
        this.postProcessing.outputNode = scenePass;
    }

    private initLights() {
        const ambientLight = new THREE.AmbientLight(0xffffff, 2.5);
        this.scene.add(ambientLight);

        this.scene.background = new THREE.Color(0x87ceeb);
    }

    private initEvents() {
        window.addEventListener('resize', () => {
            this.sizes.width = window.innerWidth;
            this.sizes.height = window.innerHeight;
            this.sizes.pixelRatio = Math.min(window.devicePixelRatio, 2);
            this.renderer.setSize(this.sizes.width, this.sizes.height);
            this.renderer.setPixelRatio(this.sizes.pixelRatio);
            this.camera.aspect = this.sizes.width / this.sizes.height;
            this.camera.updateProjectionMatrix();
        });
    }

    private animate() {
        this.controls.update();

        this.locomotive.animate();
        if (IS_DEBUG_ENABLED) {
            if (this.locomotive.speed < 0) {
                this.locomotive.reverseDistance += -this.locomotive.speed * 0.1;
            } else {
                this.locomotive.reverseDistance = 0;
            }
        }

        this.tracks.animate(this.locomotive.mesh, this.locomotive.rotate);
        if (IS_DEBUG_ENABLED) {
            // Apply reverse position AFTER tracks are updated
            if (this.locomotive.speed < 0) {
                this.tracks.snapToBackwardTrack(
                    this.locomotive.mesh,
                    this.locomotive.reverseDistance
                );
            }
        }

        if (!IS_DEBUG_ENABLED) {
            this.ground.position.z = this.locomotive.mesh.position.z;
            this.controls.target.z = this.locomotive.mesh.position.z;
        }

        this.railcars.forEach((car) => {
            car.animate();
        });

        this.postProcessing.render();
    }
}
